import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { BuildingOfficeIcon, CogIcon, MapPinIcon, ChartBarIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import api from '@/services/api';

interface HierarchyData {
  id: string;
  name: string;
  level_type: string;
  level: number;
  parent_id?: string;
  status: string;
  children?: HierarchyData[];
}

interface HierarchyAdoption {
  hierarchy_type: string;
  tenant_id: string;
  adopted_at: string;
  status: string;
}

const HierarchiesPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [businessHierarchy, setBusinessHierarchy] = useState<HierarchyData[]>([]);
  const [functionalHierarchy, setFunctionalHierarchy] = useState<HierarchyData[]>([]);
  const [geographicHierarchy, setGeographicHierarchy] = useState<HierarchyData[]>([]);
  const [adoptions, setAdoptions] = useState<HierarchyAdoption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHierarchies();
  }, []);

  const loadHierarchies = async () => {
    try {
      setLoading(true);
      
      // Load all hierarchies in parallel using new tenant-aware API
      const [businessRes, functionalRes, geographicRes] = await Promise.all([
        api.get('/hierarchy/business'),
        api.get('/hierarchy/functional'),
        api.get('/hierarchy/geographic')
      ]);

      setBusinessHierarchy(buildHierarchyTree(businessRes.data.data || businessRes.data));
      setFunctionalHierarchy(buildHierarchyTree(functionalRes.data.data || functionalRes.data));
      setGeographicHierarchy(buildHierarchyTree(geographicRes.data.data || geographicRes.data));
      setAdoptions([]); // TODO: Implement adoptions endpoint when needed

    } catch (err) {
      setError(t('pages.hierarchies.failedToLoad'));
      console.error('Error loading hierarchies:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildHierarchyTree = (data: any[]): HierarchyData[] => {
    const map = new Map();
    const roots: HierarchyData[] = [];

    // Create map of all nodes
    data.forEach(item => {
      map.set(item.id, { ...item, children: [] });
    });

    // Build tree structure
    data.forEach(item => {
      const node = map.get(item.id);
      if (item.parent_id && map.has(item.parent_id)) {
        const parent = map.get(item.parent_id);
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  };

  const renderHierarchyNode = (node: HierarchyData, level: number = 0) => (
    <div key={node.id} className="ml-4">
      <div className={`flex items-center space-x-2 py-1 ${level > 0 ? 'border-l-2 border-[var(--color-border)] pl-3' : ''}`}>
        <div className={`w-2 h-2 rounded-full ${
          node.status === 'ACTIVE' ? 'bg-[var(--color-success)]' : 'bg-[var(--color-secondary-text)] opacity-50'
        }`} />
        <span className="text-sm font-medium text-[var(--color-surface-text)]">
          {node.name}
        </span>
        <span className="text-xs text-[var(--color-secondary-text)] bg-[var(--color-secondary-bg)] px-2 py-1 rounded">
          {node.level_type}
        </span>
        {node.level > 1 && (
          <span className="text-xs text-[var(--color-secondary-text)] opacity-75">
            {t('hierarchies.level')} {node.level}
          </span>
        )}
      </div>
      {node.children && node.children.length > 0 && (
        <div className="ml-4">
          {node.children.map(child => renderHierarchyNode(child, level + 1))}
        </div>
      )}
    </div>
  );

  const getAdoptionStatus = (hierarchyType: string) => {
    const adoption = adoptions.find(a => a.hierarchy_type === hierarchyType);
    return adoption ? 'adopted' : 'not-adopted';
  };

  const handleManageHierarchy = (type: string) => {
    switch (type) {
      case 'business':
        navigate('/business-hierarchy');
        break;
      case 'functional':
        navigate('/functional-hierarchy');
        break;
      case 'geographic':
        navigate('/geographic-hierarchy');
        break;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[var(--color-surface-text)]">{t('pages.hierarchies.loadingHierarchies')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[var(--color-error)]">{t('pages.hierarchies.failedToLoad')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-secondary-text)]">
          {t('pages.hierarchies.title')}
        </h1>
        <p className="text-[var(--color-secondary-text)]">
          {t('pages.hierarchies.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Business Hierarchy */}
        <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <BuildingOfficeIcon className="h-6 w-6 text-[var(--color-primary-text)]" />
              <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
                {t('hierarchies.businessHierarchy')}
              </h2>
            </div>
            <div className="flex items-center space-x-2">
              {getAdoptionStatus('business') === 'adopted' ? (
                <CheckCircleIcon className="h-5 w-5 text-[var(--color-success)]" />
              ) : (
                <XCircleIcon className="h-5 w-5 text-[var(--color-secondary-text)] opacity-75" />
              )}
              <span className="text-sm text-[var(--color-surface-text)]">
                {getAdoptionStatus('business') === 'adopted' ? t('hierarchies.adopted') : t('hierarchies.notAdopted')}
              </span>
            </div>
          </div>
          
          <div className="mb-4">
            <div className="text-sm text-[var(--color-surface-text)] mb-2">
              {businessHierarchy.length} {t('hierarchies.levels')} • {businessHierarchy.reduce((acc, node) => acc + (node.children?.length || 0), 0)} {t('hierarchies.nodes')}
            </div>
            <div className="max-h-48 overflow-y-auto">
              {businessHierarchy.map(node => renderHierarchyNode(node))}
            </div>
          </div>

          <button
            onClick={() => handleManageHierarchy('business')}
            className="w-full bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 rounded-md hover:opacity-90 transition-opacity"
          >
            {t('hierarchies.manageBusinessHierarchy')}
          </button>
        </div>

        {/* Functional Hierarchy */}
        <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <CogIcon className="h-6 w-6 text-[var(--color-primary-text)]" />
              <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
                {t('hierarchies.functionalHierarchy')}
              </h2>
            </div>
            <div className="flex items-center space-x-2">
              {getAdoptionStatus('functional') === 'adopted' ? (
                <CheckCircleIcon className="h-5 w-5 text-[var(--color-success)]" />
              ) : (
                <XCircleIcon className="h-5 w-5 text-[var(--color-secondary-text)] opacity-75" />
              )}
              <span className="text-sm text-[var(--color-surface-text)]">
                {getAdoptionStatus('functional') === 'adopted' ? t('hierarchies.adopted') : t('hierarchies.notAdopted')}
              </span>
            </div>
          </div>
          
          <div className="mb-4">
            <div className="text-sm text-[var(--color-surface-text)] mb-2">
              {functionalHierarchy.length} {t('hierarchies.levels')} • {functionalHierarchy.reduce((acc, node) => acc + (node.children?.length || 0), 0)} {t('hierarchies.nodes')}
            </div>
            <div className="max-h-48 overflow-y-auto">
              {functionalHierarchy.map(node => renderHierarchyNode(node))}
            </div>
          </div>

          <button
            onClick={() => handleManageHierarchy('functional')}
            className="w-full bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 rounded-md hover:opacity-90 transition-opacity"
          >
            {t('hierarchies.manageFunctionalHierarchy')}
          </button>
        </div>

        {/* Geographic Hierarchy */}
        <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <MapPinIcon className="h-6 w-6 text-[var(--color-primary-text)]" />
              <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
                {t('hierarchies.geographicHierarchy')}
              </h2>
            </div>
            <div className="flex items-center space-x-2">
              {getAdoptionStatus('geographic') === 'adopted' ? (
                <CheckCircleIcon className="h-5 w-5 text-[var(--color-success)]" />
              ) : (
                <XCircleIcon className="h-5 w-5 text-[var(--color-secondary-text)] opacity-75" />
              )}
              <span className="text-sm text-[var(--color-surface-text)]">
                {getAdoptionStatus('geographic') === 'adopted' ? t('hierarchies.adopted') : t('hierarchies.notAdopted')}
              </span>
            </div>
          </div>
          
          <div className="mb-4">
            <div className="text-sm text-[var(--color-surface-text)] mb-2">
              {geographicHierarchy.length} {t('hierarchies.levels')} • {geographicHierarchy.reduce((acc, node) => acc + (node.children?.length || 0), 0)} {t('hierarchies.nodes')}
            </div>
            <div className="max-h-48 overflow-y-auto">
              {geographicHierarchy.map(node => renderHierarchyNode(node))}
            </div>
          </div>

          <button
            onClick={() => handleManageHierarchy('geographic')}
            className="w-full bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 rounded-md hover:opacity-90 transition-opacity"
          >
            {t('hierarchies.manageGeographicHierarchy')}
          </button>
        </div>
      </div>

      {/* Hierarchy Adoption Summary */}
      <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
        <div className="flex items-center space-x-2 mb-4">
          <ChartBarIcon className="h-6 w-6 text-[var(--color-primary-text)]" />
          <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
            {t('hierarchies.adoptionSummary')}
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-[var(--color-secondary-bg)] rounded-lg border border-[var(--color-border)]">
            <div className="text-2xl font-bold text-[var(--color-surface-text)]">
              {adoptions.filter(a => a.hierarchy_type === 'business').length}
            </div>
            <div className="text-sm text-[var(--color-surface-text)]">{t('hierarchies.businessAdoptions')}</div>
          </div>
          <div className="text-center p-4 bg-[var(--color-secondary-bg)] rounded-lg border border-[var(--color-border)]">
            <div className="text-2xl font-bold text-[var(--color-surface-text)]">
              {adoptions.filter(a => a.hierarchy_type === 'functional').length}
            </div>
            <div className="text-sm text-[var(--color-surface-text)]">{t('hierarchies.functionalAdoptions')}</div>
          </div>
          <div className="text-center p-4 bg-[var(--color-secondary-bg)] rounded-lg border border-[var(--color-border)]">
            <div className="text-2xl font-bold text-[var(--color-surface-text)]">
              {adoptions.filter(a => a.hierarchy_type === 'geographic').length}
            </div>
            <div className="text-sm text-[var(--color-surface-text)]">{t('hierarchies.geographicAdoptions')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HierarchiesPage;
